/**
 * Tipos e blocos visuais compartilhados da integração Tique Taque (E9-API F4 —
 * plano-integracao-tiquetaque-api.md §5): usados pelo cartão "Ponto" da
 * CompetenciaPage e pela IntegracaoTiqueTaquePage (config + histórico).
 *
 * - contagens por efeito (lançamentos de HE/adicional, movimentos de banco,
 *   afastamentos) e status da corrida;
 * - reconciliação do banco de horas (F3): resumo convergentes/divergentes +
 *   tabela por pessoa com os minutos formatados em hh:mm (format.ts);
 * - o VALOR do token nunca circula pela UI (contrato do backend).
 */
import { Badge, Table, Text } from "@mantine/core";
import { fmtDataHora, fmtHhMm } from "./format";
import { TabelaDados, Th, TdNum } from "./componentes/listas";

export interface StatusTiqueTaque {
  configurado: boolean;
  origem: "banco" | "env" | "nenhuma";
}

/** Por pessoa (espelho de ResultadoReconciliacaoPessoa do backend). */
export interface ReconciliacaoPessoa {
  funcionarioId: string;
  nome: string;
  modo: "periodo" | "ciclo_completo";
  ledgerPeriodoMin: number;
  esperadoFontePeriodoMin: number;
  deltaPeriodoMin: number;
  saldoDerivadoCicloMin?: number;
  saldoFinalFonteMin?: number;
  deltaFinalMin?: number;
  convergente: boolean;
  alertas: string[];
}

export interface ReconciliacaoResumo {
  pessoas: number;
  convergentes: number;
  divergentes: number;
  modoPeriodo: number;
  modoCicloCompleto: number;
}

/** resumoJson da corrida (tiquetaque_syncs) / corpo do POST sincronizar. */
export interface ResumoSyncTiqueTaque {
  lancamentos: number;
  movimentosBanco: number;
  afastamentos: number;
  /** M2.1 — dias do detalhe diário gravados (ponto_dias; campo aditivo). */
  diasPonto?: number;
  importacoes: { tipo: string; importacaoId: string; criada: boolean }[];
  alertas: string[];
  reconciliacaoBanco: ReconciliacaoResumo | null;
  reconciliacaoBancoDetalhe: ReconciliacaoPessoa[] | null;
}

export interface SyncTiqueTaque {
  id: string;
  competenciaId: number;
  escopo: {
    periodo: { inicioIso: string; fimIso: string };
    espelhos?: number;
    funcionariosApi?: number;
    workLeaves?: number;
  };
  status: string;
  inicioEm: string;
  fimEm: string | null;
  erro: string | null;
  resumo: ResumoSyncTiqueTaque | null;
}

/** Corpo do POST /competencias/:id/ponto/sincronizar. */
export interface ResultadoSyncTiqueTaque {
  syncId: string;
  competenciaId: number;
  geradoAgora: boolean;
  periodo: { inicioIso: string; fimIso: string };
  efeitos: { lancamentos: number; movimentosBanco: number; afastamentos: number; diasPonto?: number };
  importacoes: { tipo: string; importacaoId: string; criada: boolean }[];
  alertas: string[];
  reconciliacaoBanco: ReconciliacaoResumo | null;
  reconciliacaoBancoDetalhe: ReconciliacaoPessoa[] | null;
}

/** "7 pessoas: 7 convergentes · 0 divergentes" (ou "não reconciliado"). */
export function rotuloReconciliacao(r: ReconciliacaoResumo | null | undefined): string {
  if (!r) return "banco não reconciliado (sem hours_bank_summary)";
  return `${r.pessoas} ${r.pessoas === 1 ? "pessoa" : "pessoas"}: ${r.convergentes} convergente(s) · ${r.divergentes} divergente(s)`;
}

/** Resumo compacto dos efeitos: "3 lançamentos · 12 movimentos · 1 afastamento · 30 dias". */
export function rotuloEfeitos(e: { lancamentos: number; movimentosBanco: number; afastamentos: number; diasPonto?: number }): string {
  const base = `${e.lancamentos} lançamento(s) · ${e.movimentosBanco} movimento(s) de banco · ${e.afastamentos} afastamento(s)`;
  return e.diasPonto !== undefined ? `${base} · ${e.diasPonto} dia(s) de detalhe` : base;
}

export function BadgeStatusSync({ status }: { status: string }) {
  return (
    <Badge color={status === "concluido" ? "sillis" : status === "falha" ? "red" : "yellow"} variant="light">
      {status === "concluido" ? "concluído" : status === "falha" ? "falha" : status}
    </Badge>
  );
}

/** Tabela por pessoa da reconciliação do banco de horas (fonte × ledger, em
 *  hh:mm). Modo "período" compara o gerado do mês; "ciclo completo" também o
 *  saldo final do ciclo. O saldo interno é SEMPRE derivado (E7b) — a tabela é
 *  só conferência. */
export function BlocoReconciliacao({
  resumo,
  detalhe,
}: {
  resumo: ReconciliacaoResumo | null | undefined;
  detalhe: ReconciliacaoPessoa[] | null | undefined;
}) {
  if (!resumo) {
    return (
      <Text size="sm" c="dimmed">
        Banco de horas não reconciliado nesta corrida (nenhum espelho com hours_bank_summary e match na folha).
      </Text>
    );
  }
  return (
    <>
      <Text size="sm" mb={4}>
        <strong>Reconciliação do banco de horas:</strong> {rotuloReconciliacao(resumo)}
        {resumo.modoCicloCompleto > 0 ? ` (${resumo.modoCicloCompleto} no modo ciclo completo)` : " (modo período)"}
      </Text>
      {detalhe && detalhe.length > 0 && (
        <TabelaDados maw={860} mb="sm">
          <Table.Thead>
            <Table.Tr>
              <Th>Pessoa</Th>
              <Th>Modo</Th>
              <Th num>Ledger (período)</Th>
              <Th num>Fonte (período)</Th>
              <Th num>Delta</Th>
              <Th>Ciclo (derivado × fonte)</Th>
              <Th>Status</Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {detalhe.map((p) => (
              <Table.Tr key={p.funcionarioId}>
                <Table.Td>
                  <Text size="sm">{p.nome}</Text>
                </Table.Td>
                <Table.Td>
                  <Text size="xs" c="dimmed">
                    {p.modo === "ciclo_completo" ? "ciclo completo" : "período"}
                  </Text>
                </Table.Td>
                <TdNum>{fmtHhMm(p.ledgerPeriodoMin)}</TdNum>
                <TdNum>{fmtHhMm(p.esperadoFontePeriodoMin)}</TdNum>
                <TdNum>
                  <Text size="sm" c={p.deltaPeriodoMin !== 0 ? "red.7" : undefined} component="span">
                    {fmtHhMm(p.deltaPeriodoMin)}
                  </Text>
                </TdNum>
                <Table.Td>
                  <Text size="sm">
                    {p.modo === "ciclo_completo"
                      ? `${fmtHhMm(p.saldoDerivadoCicloMin)} × ${fmtHhMm(p.saldoFinalFonteMin)}${p.deltaFinalMin ? ` (Δ ${fmtHhMm(p.deltaFinalMin)})` : ""}`
                      : "—"}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Badge variant="light" color={p.convergente ? "sillis" : "red"}>
                    {p.convergente ? "convergente" : "divergente"}
                  </Badge>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </TabelaDados>
      )}
    </>
  );
}

/** Linha "Quando — status" legível de uma corrida. */
export function rotuloQuandoSync(s: SyncTiqueTaque): string {
  return `${fmtDataHora(s.inicioEm)}`;
}
